import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Collapse,
  Input,
  Row,
  Col,
  Label,
  FormFeedback,
} from "reactstrap"
import { useTranslation } from "react-i18next"

// Categories configuration matching Figma order exactly
// columns: number of columns per row based on Figma design
const categoriesConfig = [
  {
    key: "gender",
    labelKey: "demographic.gender",
    columns: 2,
    options: [
      { value: "male", labelKey: "demographic.gender.male" },
      { value: "female", labelKey: "demographic.gender.female" },
    ],
  },
  {
    key: "age",
    labelKey: "demographic.age",
    columns: 2,
    options: [
      { value: "under18", labelKey: "demographic.age.under18" },
      { value: "18-25", labelKey: "demographic.age.18-25" },
      { value: "26-29", labelKey: "demographic.age.26-29" },
      { value: "30-35", labelKey: "demographic.age.30-35" },
      { value: "36-39", labelKey: "demographic.age.36-39" },
      { value: "40-45", labelKey: "demographic.age.40-45" },
      { value: "46-49", labelKey: "demographic.age.46-49" },
      { value: "50plus", labelKey: "demographic.age.50plus" },
    ],
  },
  {
    key: "marriage",
    labelKey: "demographic.marriage",
    columns: 2,
    options: [
      { value: "single", labelKey: "demographic.marriage.single" },
      { value: "marriedNoChildren", labelKey: "demographic.marriage.marriedNoChildren" },
      { value: "marriedWithChildren", labelKey: "demographic.marriage.marriedWithChildren" },
      { value: "divorced", labelKey: "demographic.marriage.divorced" },
    ],
  },
  {
    key: "education",
    labelKey: "demographic.education",
    columns: 3,
    options: [
      { value: "juniorHighBelow", labelKey: "demographic.education.juniorHighBelow" },
      { value: "highSchool", labelKey: "demographic.education.highSchool" },
      { value: "juniorCollege", labelKey: "demographic.education.juniorCollege" },
      { value: "bachelor", labelKey: "demographic.education.bachelor" },
      { value: "masterAbove", labelKey: "demographic.education.masterAbove" },
    ],
  },
  {
    key: "personalMonthlyIncome",
    labelKey: "demographic.personalMonthlyIncome",
    columns: 4,
    options: [
      { value: "below5000", labelKey: "demographic.personalMonthlyIncome.below5000" },
      { value: "5000-10000", labelKey: "demographic.personalMonthlyIncome.5000-10000" },
      { value: "10000-15000", labelKey: "demographic.personalMonthlyIncome.10000-15000" },
      { value: "15000-20000", labelKey: "demographic.personalMonthlyIncome.15000-20000" },
      { value: "20000-30000", labelKey: "demographic.personalMonthlyIncome.20000-30000" },
      { value: "30000-50000", labelKey: "demographic.personalMonthlyIncome.30000-50000" },
      { value: "50000plus", labelKey: "demographic.personalMonthlyIncome.50000plus" },
    ],
  },
  {
    key: "familyMonthlyIncome",
    labelKey: "demographic.familyMonthlyIncome",
    columns: 5,
    options: [
      { value: "below5000", labelKey: "demographic.familyMonthlyIncome.below5000" },
      { value: "5000-7000", labelKey: "demographic.familyMonthlyIncome.5000-7000" },
      { value: "7000-10000", labelKey: "demographic.familyMonthlyIncome.7000-10000" },
      { value: "10000-20000", labelKey: "demographic.familyMonthlyIncome.10000-20000" },
      { value: "20000-30000", labelKey: "demographic.familyMonthlyIncome.20000-30000" },
      { value: "30000-50000", labelKey: "demographic.familyMonthlyIncome.30000-50000" },
      { value: "50000-100000", labelKey: "demographic.familyMonthlyIncome.50000-100000" },
      { value: "100000-200000", labelKey: "demographic.familyMonthlyIncome.100000-200000" },
      { value: "200000plus", labelKey: "demographic.familyMonthlyIncome.200000plus" },
    ],
  },
  {
    key: "avgMonthlySpending",
    labelKey: "demographic.avgMonthlySpending",
    columns: 4,
    options: [
      { value: "below5000", labelKey: "demographic.avgMonthlySpending.below5000" },
      { value: "5000-7000", labelKey: "demographic.avgMonthlySpending.5000-7000" },
      { value: "7000-10000", labelKey: "demographic.avgMonthlySpending.7000-10000" },
      { value: "10000-20000", labelKey: "demographic.avgMonthlySpending.10000-20000" },
      { value: "20000-30000", labelKey: "demographic.avgMonthlySpending.20000-30000" },
      { value: "30000-50000", labelKey: "demographic.avgMonthlySpending.30000-50000" },
      { value: "50000plus", labelKey: "demographic.avgMonthlySpending.50000plus" },
    ],
  },
  {
    key: "spendingHabits",
    labelKey: "demographic.spendingHabits",
    columns: 3,
    options: [
      { value: "brandLoyal", labelKey: "demographic.spendingHabits.brandLoyal" },
      { value: "rational", labelKey: "demographic.spendingHabits.rational" },
      { value: "priceSensitive", labelKey: "demographic.spendingHabits.priceSensitive" },
      { value: "impulse", labelKey: "demographic.spendingHabits.impulse" },
      { value: "selective", labelKey: "demographic.spendingHabits.selective" },
      { value: "influenceable", labelKey: "demographic.spendingHabits.influenceable" },
    ],
  },
  {
    key: "interestType",
    labelKey: "demographic.interestType",
    columns: 4,
    options: [
      { value: "miningLivestock", labelKey: "demographic.interestType.miningLivestock" },
      { value: "agricultureForestry", labelKey: "demographic.interestType.agricultureForestry" },
      { value: "internetTech", labelKey: "demographic.interestType.internetTech" },
      { value: "healthcarePharma", labelKey: "demographic.interestType.healthcarePharma" },
      { value: "educationTraining", labelKey: "demographic.interestType.educationTraining" },
      { value: "accommodationCatering", labelKey: "demographic.interestType.accommodationCatering" },
      { value: "retailWholesale", labelKey: "demographic.interestType.retailWholesale" },
      { value: "financeInsurance", labelKey: "demographic.interestType.financeInsurance" },
      { value: "transportLogistics", labelKey: "demographic.interestType.transportLogistics" },
      { value: "cultureEntertainment", labelKey: "demographic.interestType.cultureEntertainment" },
      { value: "constructionManufacturing", labelKey: "demographic.interestType.constructionManufacturing" },
      { value: "scientificResearch", labelKey: "demographic.interestType.scientificResearch" },
      { value: "aviationTourism", labelKey: "demographic.interestType.aviationTourism" },
    ],
  },
  {
    key: "hobbiesInterests",
    labelKey: "demographic.hobbiesInterests",
    columns: 4,
    options: [
      { value: "travel", labelKey: "demographic.hobbiesInterests.travel" },
      { value: "movies", labelKey: "demographic.hobbiesInterests.movies" },
      { value: "sports", labelKey: "demographic.hobbiesInterests.sports" },
      { value: "food", labelKey: "demographic.hobbiesInterests.food" },
      { value: "animeGames", labelKey: "demographic.hobbiesInterests.animeGames" },
      { value: "reading", labelKey: "demographic.hobbiesInterests.reading" },
      { value: "music", labelKey: "demographic.hobbiesInterests.music" },
      { value: "photography", labelKey: "demographic.hobbiesInterests.photography" },
      { value: "boardGames", labelKey: "demographic.hobbiesInterests.boardGames" },
      { value: "pets", labelKey: "demographic.hobbiesInterests.pets" },
      { value: "handicrafts", labelKey: "demographic.hobbiesInterests.handicrafts" },
      { value: "collections", labelKey: "demographic.hobbiesInterests.collections" },
      { value: "newsInfo", labelKey: "demographic.hobbiesInterests.newsInfo" },
      { value: "paidVideoStreaming", labelKey: "demographic.hobbiesInterests.paidVideoStreaming" },
      { value: "socialNetworking", labelKey: "demographic.hobbiesInterests.socialNetworking" },
      { value: "travelTransport", labelKey: "demographic.hobbiesInterests.travelTransport" },
    ],
  },
]

// City configuration with hierarchical structure (Region > Province > City)
const cityConfig = {
  labelKey: "demographic.city",
  regions: [
    {
      key: "eastChina",
      labelKey: "demographic.region.eastChina",
      provinces: [
        {
          key: "anhui",
          labelKey: "demographic.province.anhui",
          cities: ["hefei", "wuhu", "bengbu", "huainan", "maanshan", "huaibei", "tongling", "anqing", "huangshan"],
        },
        {
          key: "jiangsu",
          labelKey: "demographic.province.jiangsu",
          cities: ["nanjing", "suzhou", "wuxi", "changzhou", "nantong", "yangzhou", "zhenjiang", "taizhou_js", "yancheng"],
        },
        {
          key: "zhejiang",
          labelKey: "demographic.province.zhejiang",
          cities: ["hangzhou", "ningbo", "wenzhou", "jiaxing", "huzhou", "shaoxing", "jinhua", "taizhou_zj", "lishui"],
        },
        {
          key: "shanghai",
          labelKey: "demographic.province.shanghai",
          cities: ["shanghai"],
        },
      ],
    },
    {
      key: "northChina",
      labelKey: "demographic.region.northChina",
      provinces: [
        {
          key: "beijing",
          labelKey: "demographic.province.beijing",
          cities: ["beijing"],
        },
        {
          key: "tianjin",
          labelKey: "demographic.province.tianjin",
          cities: ["tianjin"],
        },
        {
          key: "hebei",
          labelKey: "demographic.province.hebei",
          cities: ["shijiazhuang", "tangshan", "qinhuangdao", "handan", "xingtai", "baoding", "zhangjiakou", "chengde", "cangzhou"],
        },
        {
          key: "shanxi",
          labelKey: "demographic.province.shanxi",
          cities: ["taiyuan", "datong", "yangquan", "changzhi", "jincheng", "shuozhou", "jinzhong", "yuncheng", "xinzhou"],
        },
      ],
    },
    {
      key: "northeast",
      labelKey: "demographic.region.northeast",
      provinces: [
        {
          key: "liaoning",
          labelKey: "demographic.province.liaoning",
          cities: ["shenyang", "dalian", "anshan", "fushun", "benxi", "dandong", "jinzhou", "yingkou", "fuxin"],
        },
        {
          key: "jilin",
          labelKey: "demographic.province.jilin",
          cities: ["changchun", "jilin_city", "siping", "liaoyuan", "tonghua", "baishan", "songyuan", "baicheng", "yanbian"],
        },
        {
          key: "heilongjiang",
          labelKey: "demographic.province.heilongjiang",
          cities: ["harbin", "qiqihar", "jixi", "hegang", "shuangyashan", "daqing", "yichun", "jiamusi", "mudanjiang"],
        },
      ],
    },
    {
      key: "centralChina",
      labelKey: "demographic.region.centralChina",
      provinces: [
        {
          key: "henan",
          labelKey: "demographic.province.henan",
          cities: ["zhengzhou", "kaifeng", "luoyang", "pingdingshan", "anyang_hn", "hebi", "xinxiang", "jiaozuo", "puyang"],
        },
        {
          key: "hubei",
          labelKey: "demographic.province.hubei",
          cities: ["wuhan", "huangshi", "shiyan", "yichang", "xiangyang", "ezhou", "jingmen", "xiaogan", "jingzhou"],
        },
        {
          key: "hunan",
          labelKey: "demographic.province.hunan",
          cities: ["changsha", "zhuzhou", "xiangtan", "hengyang", "shaoyang", "yueyang", "changde", "zhangjiajie", "yiyang"],
        },
      ],
    },
    {
      key: "southChina",
      labelKey: "demographic.region.southChina",
      provinces: [
        {
          key: "guangdong",
          labelKey: "demographic.province.guangdong",
          cities: ["guangzhou", "shenzhen", "zhuhai", "shantou", "foshan", "shaoguan", "zhanjiang", "zhaoqing", "jiangmen"],
        },
        {
          key: "guangxi",
          labelKey: "demographic.province.guangxi",
          cities: ["nanning", "liuzhou", "guilin", "wuzhou", "beihai", "fangchenggang", "qinzhou", "guigang", "yulin"],
        },
        {
          key: "hainan",
          labelKey: "demographic.province.hainan",
          cities: ["haikou", "sanya", "sansha", "danzhou"],
        },
      ],
    },
    {
      key: "southwest",
      labelKey: "demographic.region.southwest",
      provinces: [
        {
          key: "sichuan",
          labelKey: "demographic.province.sichuan",
          cities: ["chengdu", "mianyang", "zigong", "panzhihua", "luzhou", "deyang", "guangyuan", "suining", "neijiang"],
        },
        {
          key: "chongqing",
          labelKey: "demographic.province.chongqing",
          cities: ["chongqing"],
        },
        {
          key: "guizhou",
          labelKey: "demographic.province.guizhou",
          cities: ["guiyang", "liupanshui", "zunyi", "anshun", "bijie", "tongren"],
        },
        {
          key: "yunnan",
          labelKey: "demographic.province.yunnan",
          cities: ["kunming", "qujing", "yuxi", "baoshan", "zhaotong", "lijiang", "puer", "lincang"],
        },
      ],
    },
    {
      key: "northwest",
      labelKey: "demographic.region.northwest",
      provinces: [
        {
          key: "shaanxi",
          labelKey: "demographic.province.shaanxi",
          cities: ["xian", "tongchuan", "baoji", "xianyang", "weinan", "yanan", "hanzhong", "yulin_sn", "ankang"],
        },
        {
          key: "gansu",
          labelKey: "demographic.province.gansu",
          cities: ["lanzhou", "jiayuguan", "jinchang", "baiyin", "tianshui", "wuwei", "zhangye", "pingliang", "jiuquan"],
        },
        {
          key: "qinghai",
          labelKey: "demographic.province.qinghai",
          cities: ["xining", "haidong"],
        },
        {
          key: "ningxia",
          labelKey: "demographic.province.ningxia",
          cities: ["yinchuan", "shizuishan", "wuzhong", "guyuan", "zhongwei"],
        },
        {
          key: "xinjiang",
          labelKey: "demographic.province.xinjiang",
          cities: ["urumqi", "karamay", "turpan", "hami"],
        },
      ],
    },
  ],
}

// Additional categories that belong to Basic Demographic (matching Chinese version)
const additionalBasicCategories = [
  {
    key: "appCategories",
    labelKey: "demographic.appCategories",
    columns: 4,
    options: [
      { value: "gaming", labelKey: "demographic.appCategories.gaming" },
      { value: "reading", labelKey: "demographic.appCategories.reading" },
      { value: "wealthMgmt", labelKey: "demographic.appCategories.wealthMgmt" },
      { value: "shopping", labelKey: "demographic.appCategories.shopping" },
      { value: "education", labelKey: "demographic.appCategories.education" },
      { value: "shortVideos", labelKey: "demographic.appCategories.shortVideos" },
      { value: "photoDesign", labelKey: "demographic.appCategories.photoDesign" },
      { value: "lifestyle", labelKey: "demographic.appCategories.lifestyle" },
      { value: "smartOffice", labelKey: "demographic.appCategories.smartOffice" },
    ],
  },
  {
    key: "livingArrangement",
    labelKey: "demographic.livingArrangement",
    columns: 3,
    options: [
      { value: "liveAlone", labelKey: "demographic.livingArrangement.liveAlone" },
      { value: "withPartner", labelKey: "demographic.livingArrangement.withPartner" },
      { value: "withSpouseChildren", labelKey: "demographic.livingArrangement.withSpouseChildren" },
      { value: "withParents", labelKey: "demographic.livingArrangement.withParents" },
      { value: "withParentsSpouseChildren", labelKey: "demographic.livingArrangement.withParentsSpouseChildren" },
    ],
  },
  {
    key: "transportation",
    labelKey: "demographic.transportation",
    columns: 4,
    options: [
      { value: "privateCar", labelKey: "demographic.transportation.privateCar" },
      { value: "carSharing", labelKey: "demographic.transportation.carSharing" },
      { value: "taxi", labelKey: "demographic.transportation.taxi" },
      { value: "publicTransport", labelKey: "demographic.transportation.publicTransport" },
      { value: "bicycle", labelKey: "demographic.transportation.bicycle" },
      { value: "motorcycle", labelKey: "demographic.transportation.motorcycle" },
      { value: "ebike", labelKey: "demographic.transportation.ebike" },
    ],
  },
]

// Car related categories - matching Chinese version
const carRelatedCategories = [
  {
    key: "carOwner",
    labelKey: "demographic.carOwner",
    columns: 2,
    options: [
      { value: "yes", labelKey: "demographic.carOwner.yes" },
      { value: "no", labelKey: "demographic.carOwner.no" },
    ],
  },
  {
    key: "fuelType",
    labelKey: "demographic.fuelType",
    columns: 4,
    options: [
      { value: "gasoline", labelKey: "demographic.fuelType.gasoline" },
      { value: "hev", labelKey: "demographic.fuelType.hev" },
      { value: "phev", labelKey: "demographic.fuelType.phev" },
      { value: "ev", labelKey: "demographic.fuelType.ev" },
    ],
  },
  {
    key: "carType",
    labelKey: "demographic.carType",
    columns: 4,
    options: [
      { value: "sedan", labelKey: "demographic.carType.sedan" },
      { value: "suv", labelKey: "demographic.carType.suv" },
      { value: "mpv", labelKey: "demographic.carType.mpv" },
      { value: "sportsCar", labelKey: "demographic.carType.sportsCar" },
    ],
  },
  {
    key: "paymentMethod",
    labelKey: "demographic.paymentMethod",
    columns: 2,
    options: [
      { value: "fullPayment", labelKey: "demographic.paymentMethod.fullPayment" },
      { value: "installment", labelKey: "demographic.paymentMethod.installment" },
    ],
  },
  {
    key: "purchaseTime",
    labelKey: "demographic.purchaseTime",
    columns: 3,
    options: [
      { value: "within1year", labelKey: "demographic.purchaseTime.within1year" },
      { value: "1-2years", labelKey: "demographic.purchaseTime.1-2years" },
      { value: "2-3years", labelKey: "demographic.purchaseTime.2-3years" },
      { value: "3-5years", labelKey: "demographic.purchaseTime.3-5years" },
      { value: "5-8years", labelKey: "demographic.purchaseTime.5-8years" },
      { value: "8plusYears", labelKey: "demographic.purchaseTime.8plusYears" },
    ],
  },
  {
    key: "mileageDriven",
    labelKey: "demographic.mileageDriven",
    columns: 3,
    options: [
      { value: "below10000", labelKey: "demographic.mileageDriven.below10000" },
      { value: "10000-30000", labelKey: "demographic.mileageDriven.10000-30000" },
      { value: "30000-50000", labelKey: "demographic.mileageDriven.30000-50000" },
      { value: "50000-100000", labelKey: "demographic.mileageDriven.50000-100000" },
      { value: "100000plus", labelKey: "demographic.mileageDriven.100000plus" },
    ],
  },
  {
    key: "purchaseStatus",
    labelKey: "demographic.purchaseStatus",
    columns: 2,
    options: [
      { value: "newCar", labelKey: "demographic.purchaseStatus.newCar" },
      { value: "usedCar", labelKey: "demographic.purchaseStatus.usedCar" },
    ],
  },
  {
    key: "purchaseChannel",
    labelKey: "demographic.purchaseChannel",
    columns: 3,
    options: [
      { value: "4sStore", labelKey: "demographic.purchaseChannel.4sStore" },
      { value: "tradingMarket", labelKey: "demographic.purchaseChannel.tradingMarket" },
      { value: "autoShow", labelKey: "demographic.purchaseChannel.autoShow" },
      { value: "groupBuying", labelKey: "demographic.purchaseChannel.groupBuying" },
      { value: "onlinePlatform", labelKey: "demographic.purchaseChannel.onlinePlatform" },
    ],
  },
  {
    key: "vehiclesOwned",
    labelKey: "demographic.vehiclesOwned",
    columns: 4,
    options: [
      { value: "1vehicle", labelKey: "demographic.vehiclesOwned.1vehicle" },
      { value: "2vehicles", labelKey: "demographic.vehiclesOwned.2vehicles" },
      { value: "3vehicles", labelKey: "demographic.vehiclesOwned.3vehicles" },
      { value: "moreThan3", labelKey: "demographic.vehiclesOwned.moreThan3" },
    ],
  },
]

// Planned purchase categories - matching Chinese version exactly
const plannedPurchaseCategories = [
  {
    key: "plannedPurchaseTime",
    labelKey: "demographic.plannedPurchaseTime",
    columns: 4,
    options: [
      { value: "within6months", labelKey: "demographic.plannedPurchaseTime.within6months" },
      { value: "within1year", labelKey: "demographic.plannedPurchaseTime.within1year" },
      { value: "within2years", labelKey: "demographic.plannedPurchaseTime.within2years" },
      { value: "2plusYears", labelKey: "demographic.plannedPurchaseTime.2plusYears" },
    ],
  },
  {
    key: "plannedPowertrainType",
    labelKey: "demographic.plannedPowertrainType",
    columns: 3,
    options: [
      { value: "fuelVehicle", labelKey: "demographic.plannedPowertrainType.fuelVehicle" },
      { value: "hybrid", labelKey: "demographic.plannedPowertrainType.hybrid" },
      { value: "bev", labelKey: "demographic.plannedPowertrainType.bev" },
    ],
  },
  {
    key: "plannedVehicleType",
    labelKey: "demographic.plannedVehicleType",
    columns: 3,
    options: [
      { value: "sedan", labelKey: "demographic.plannedVehicleType.sedan" },
      { value: "suv", labelKey: "demographic.plannedVehicleType.suv" },
      { value: "mpv", labelKey: "demographic.plannedVehicleType.mpv" },
    ],
  },
  {
    key: "plannedPurchaseStatus",
    labelKey: "demographic.plannedPurchaseStatus",
    columns: 2,
    options: [
      { value: "newCar", labelKey: "demographic.plannedPurchaseStatus.newCar" },
      { value: "usedCar", labelKey: "demographic.plannedPurchaseStatus.usedCar" },
    ],
  },
  {
    key: "carInfoSources",
    labelKey: "demographic.carInfoSources",
    columns: 4,
    options: [
      { value: "tvRadio", labelKey: "demographic.carInfoSources.tvRadio" },
      { value: "videoMedia", labelKey: "demographic.carInfoSources.videoMedia" },
      { value: "socialMedia", labelKey: "demographic.carInfoSources.socialMedia" },
      { value: "liveStreaming", labelKey: "demographic.carInfoSources.liveStreaming" },
      { value: "offlineStore", labelKey: "demographic.carInfoSources.offlineStore" },
      { value: "carMarket", labelKey: "demographic.carInfoSources.carMarket" },
      { value: "internationalAutoShow", labelKey: "demographic.carInfoSources.internationalAutoShow" },
      { value: "friendsFamily", labelKey: "demographic.carInfoSources.friendsFamily" },
      { value: "autoForums", labelKey: "demographic.carInfoSources.autoForums" },
      { value: "carMagazines", labelKey: "demographic.carInfoSources.carMagazines" },
      { value: "portalWebsites", labelKey: "demographic.carInfoSources.portalWebsites" },
    ],
  },
  {
    key: "purchaseDecisionFactors",
    labelKey: "demographic.purchaseDecisionFactors",
    columns: 4,
    options: [
      { value: "dailyLifeNeeds", labelKey: "demographic.purchaseDecisionFactors.dailyLifeNeeds" },
      { value: "workLocation", labelKey: "demographic.purchaseDecisionFactors.workLocation" },
      { value: "financialImprovement", labelKey: "demographic.purchaseDecisionFactors.financialImprovement" },
      { value: "householdSize", labelKey: "demographic.purchaseDecisionFactors.householdSize" },
      { value: "familyStructure", labelKey: "demographic.purchaseDecisionFactors.familyStructure" },
      { value: "socialNeeds", labelKey: "demographic.purchaseDecisionFactors.socialNeeds" },
      { value: "rideHailingDriver", labelKey: "demographic.purchaseDecisionFactors.rideHailingDriver" },
      { value: "statusImage", labelKey: "demographic.purchaseDecisionFactors.statusImage" },
    ],
  },
]

// Styles matching Figma design exactly
const styles = `
  .demo-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03), 0 2px 2px -1px rgba(10,13,18,0.04);
    margin-bottom: 16px;
    overflow: hidden;
  }
  .demo-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    background: #e9eaeb;
    cursor: pointer;
    border-radius: 12px 12px 0 0;
  }
  .demo-card-header h6 {
    margin: 0;
    font-weight: 600;
    color: #181d27;
    font-size: 18px;
    line-height: 28px;
  }
  .demo-card-header i {
    font-size: 24px;
    color: #181d27;
  }
  .demo-rows-container {
    flex: 1;
    padding: 0;
  }
  .demo-row {
    display: flex;
    align-items: flex-start;
    padding: 12px 0;
    border-bottom: 1px solid #d5d7da;
    gap: 32px;
  }
  .demo-row:last-child {
    border-bottom: none;
  }
  .demo-row-label {
    min-width: 120px;
    width: 120px;
    font-weight: 400;
    color: #535862;
    font-size: 14px;
    line-height: 20px;
    flex-shrink: 0;
  }
  .demo-row-options {
    flex: 1;
    display: grid;
    gap: 12px 24px;
  }
  .demo-row-options.cols-2 {
    grid-template-columns: repeat(2, auto);
    justify-content: start;
    gap: 12px 48px;
  }
  .demo-row-options.cols-3 {
    grid-template-columns: repeat(3, auto);
    justify-content: start;
    gap: 12px 32px;
  }
  .demo-row-options.cols-4 {
    grid-template-columns: repeat(4, auto);
    justify-content: start;
    gap: 12px 32px;
  }
  .demo-row-options.cols-5 {
    grid-template-columns: repeat(5, auto);
    justify-content: start;
    gap: 12px 24px;
  }
  .demo-row-options.cols-6 {
    grid-template-columns: repeat(6, auto);
    justify-content: start;
    gap: 12px 20px;
  }
  .demo-checkbox {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    user-select: none;
    gap: 8px;
  }
  .demo-checkbox-box {
    width: 16px;
    height: 16px;
    border: 1px solid #d5d7da;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    background: #fff;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .demo-checkbox-box.checked {
    background: #00ac7c;
    border-color: #00ac7c;
  }
  .demo-checkbox-box i {
    color: #fff;
    font-size: 11px;
  }
  .demo-checkbox-label {
    font-size: 14px;
    line-height: 20px;
    color: #414651;
  }
  .demo-section {
    border-top: 1px solid #e9eaeb;
  }
  .demo-section:first-child {
    border-top: none;
  }
  .demo-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    cursor: pointer;
    background: #f5f5f5;
  }
  .demo-section-header:hover {
    background: #f0f0f0;
  }
  .demo-section-header h6 {
    margin: 0;
    font-weight: 600;
    color: #414651;
    font-size: 16px;
    line-height: 24px;
  }
  .demo-section-header i {
    font-size: 24px;
    color: #535862;
  }
  .demo-section-body {
    background: #fff;
    border-top: 1px solid #e9eaeb;
  }
  .demo-section-content {
    padding: 20px 24px 24px 24px;
  }
  .specific-requirement-section {
    padding: 20px 24px 24px 24px;
    border-top: 1px solid #e9eaeb;
    background: #fff;
  }
  .specific-requirement-title {
    font-weight: 600;
    color: #414651;
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 8px;
  }
  .specific-requirement-desc {
    font-size: 14px;
    color: #535862;
    margin-bottom: 20px;
  }
  .demo-textarea {
    width: 100%;
    border: 1px solid #d5d7da;
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 16px;
    line-height: 24px;
    resize: vertical;
    min-height: 180px;
    box-shadow: 0 1px 2px rgba(10,13,18,0.05);
  }
  .demo-textarea:focus {
    border-color: #00ac7c;
    outline: none;
    box-shadow: 0 0 0 4px rgba(0,172,124,0.1);
  }
  .demo-textarea::placeholder {
    color: #717680;
  }
  .demo-delete-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: #fff;
    border: 1px solid #fda29b;
    border-radius: 8px;
    color: #b42318;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 1px 2px rgba(10,13,18,0.05);
  }
  .demo-delete-btn:hover {
    background: #fef3f2;
    border-color: #f97066;
  }
  .demo-delete-btn i {
    font-size: 20px;
  }
  .demo-footer {
    padding: 16px 24px 24px 24px;
    display: flex;
    justify-content: flex-end;
    background: #fff;
  }
  /* City Section Styles - Matching Figma */
  .city-section {
    padding-bottom: 24px;
  }
  .city-row {
    display: flex;
    align-items: flex-start;
    padding: 12px 0;
    gap: 16px;
  }
  .city-content {
    flex: 1;
  }
  .city-region-wrapper {
    background: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 12px;
  }
  .city-region-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
  }
  .city-region-header:hover {
    background: #f0f0f0;
    border-radius: 8px;
  }
  .city-region-content {
    padding: 0 16px 16px 16px;
  }
  .city-province-wrapper {
    background: #fafafa;
    border-radius: 8px;
    margin-bottom: 12px;
  }
  .city-province-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
  }
  .city-province-header:hover {
    background: #f5f5f5;
    border-radius: 8px;
  }
  .city-province-content {
    padding: 0 16px 16px 16px;
    border-top: 1px solid #d5d7da;
    margin-top: 8px;
    padding-top: 16px;
  }
  .city-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px 48px;
  }
  .city-chevron {
    font-size: 20px;
    color: #535862;
    transition: transform 0.2s;
  }
  .city-chevron.open {
    transform: rotate(180deg);
  }
`

// Custom Checkbox Component
const CustomCheckbox = ({ checked, onChange, disabled, label }) => (
  <div
    className="demo-checkbox"
    onClick={() => !disabled && onChange(!checked)}
    style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
  >
    <div className={`demo-checkbox-box ${checked ? "checked" : ""}`}>
      {checked && <i className="bx bx-check" />}
    </div>
    <span className="demo-checkbox-label">{label}</span>
  </div>
)

CustomCheckbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
}

const DemographicCard = ({
  card,
  index,
  pageView,
  onToggle,
  onUpdate,
  onDelete,
  countryTarget,
}) => {
  const { t } = useTranslation()
  
  // Check if the selected country is China (has full demographic options)
  const isChina = countryTarget === "cn"

  const [openSections, setOpenSections] = useState({
    basicDemographic: true,
    carRelated: false,
    carPlannedPurchase: false,
    specificRequirement: false,
  })

  // State for city expand/collapse
  const [openRegions, setOpenRegions] = useState({})
  const [openProvinces, setOpenProvinces] = useState({})

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const toggleRegion = (regionKey) => {
    setOpenRegions(prev => ({ ...prev, [regionKey]: !prev[regionKey] }))
  }

  const toggleProvince = (provinceKey) => {
    setOpenProvinces(prev => ({ ...prev, [provinceKey]: !prev[provinceKey] }))
  }

  const handleCheckboxChange = (category, optionValue, checked) => {
    const updatedCategory = { ...card[category], [optionValue]: checked }
    onUpdate(card.id, category, updatedCategory)
  }

  // Handle city checkbox change with nested path
  const handleCityChange = (regionKey, provinceKey, city, checked) => {
    const cityPath = `${regionKey}.${provinceKey}.${city}`
    const updatedCities = { ...card.cities, [cityPath]: checked }
    onUpdate(card.id, "cities", updatedCities)
  }

  // Handle province checkbox (select/deselect all cities in province)
  const handleProvinceChange = (regionKey, province, checked) => {
    const updatedCities = { ...card.cities }
    province.cities.forEach(city => {
      const cityPath = `${regionKey}.${province.key}.${city}`
      updatedCities[cityPath] = checked
    })
    onUpdate(card.id, "cities", updatedCities)
  }

  // Handle region checkbox (select/deselect all cities in region)
  const handleRegionChange = (region, checked) => {
    const updatedCities = { ...card.cities }
    region.provinces.forEach(province => {
      province.cities.forEach(city => {
        const cityPath = `${region.key}.${province.key}.${city}`
        updatedCities[cityPath] = checked
      })
    })
    onUpdate(card.id, "cities", updatedCities)
  }

  // Check if region is fully selected
  const isRegionSelected = (region) => {
    return region.provinces.every(province =>
      province.cities.every(city => card.cities?.[`${region.key}.${province.key}.${city}`])
    )
  }

  // Check if province is fully selected
  const isProvinceSelected = (regionKey, province) => {
    return province.cities.every(city => card.cities?.[`${regionKey}.${province.key}.${city}`])
  }

  const isDisabled = pageView === "view"

  // Render a single row with label and checkboxes
  const renderRow = (category) => (
    <div key={category.key} className="demo-row">
      <div className="demo-row-label">{t(category.labelKey)}:</div>
      <div className={`demo-row-options cols-${category.columns || 4}`}>
        {category.options.map(option => (
          <CustomCheckbox
            key={option.value}
            checked={card[category.key]?.[option.value] || false}
            onChange={checked => handleCheckboxChange(category.key, option.value, checked)}
            disabled={isDisabled}
            label={t(option.labelKey)}
          />
        ))}
      </div>
    </div>
  )

  // Render city section with hierarchical structure
  const renderCitySection = () => (
    <div className="city-section">
      <div className="city-row">
        <div className="demo-row-label">{t(cityConfig.labelKey)}:</div>
        <div className="city-content">
          {cityConfig.regions.map(region => (
            <div key={region.key} className="city-region-wrapper">
              <div className="city-region-header" onClick={() => toggleRegion(region.key)}>
                <CustomCheckbox
                  checked={isRegionSelected(region)}
                  onChange={checked => handleRegionChange(region, checked)}
                  disabled={isDisabled}
                  label={t(region.labelKey)}
                />
                <i className={`bx bx-chevron-down city-chevron ${openRegions[region.key] ? "open" : ""}`} />
              </div>
              <Collapse isOpen={openRegions[region.key]}>
                <div className="city-region-content">
                  {region.provinces.map(province => (
                    <div key={province.key} className="city-province-wrapper">
                      <div className="city-province-header" onClick={() => toggleProvince(province.key)}>
                        <CustomCheckbox
                          checked={isProvinceSelected(region.key, province)}
                          onChange={checked => handleProvinceChange(region.key, province, checked)}
                          disabled={isDisabled}
                          label={t(province.labelKey)}
                        />
                        <i className={`bx bx-chevron-down city-chevron ${openProvinces[province.key] ? "open" : ""}`} />
                      </div>
                      <Collapse isOpen={openProvinces[province.key]}>
                        <div className="city-province-content">
                          <div className="city-grid">
                            {province.cities.map(cityKey => (
                              <CustomCheckbox
                                key={cityKey}
                                checked={card.cities?.[`${region.key}.${province.key}.${cityKey}`] || false}
                                onChange={checked => handleCityChange(region.key, province.key, cityKey, checked)}
                                disabled={isDisabled}
                                label={t(`demographic.city.${cityKey}`)}
                              />
                            ))}
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <style>{styles}</style>
      <div className="demo-card">
        {/* Card Header */}
        <div className="demo-card-header" onClick={() => onToggle(card.id)}>
          <h6>Group {index + 1}</h6>
          <i className={`bx ${card.isOpen ? "bx-chevron-up" : "bx-chevron-down"}`} />
        </div>

        {/* Card Content */}
        <Collapse isOpen={card.isOpen}>
          <div>
            {/* China-specific sections - only shown when countryTarget is "cn" */}
            {isChina && (
              <>
                {/* Basic Demographic Section - Main categories matching Figma order */}
                <div className="demo-section">
                  <div className="demo-section-header" onClick={() => toggleSection("basicDemographic")}>
                    <h6>{t("demographic.basicDemographic")}</h6>
                    <i className={`bx ${openSections.basicDemographic ? "bx-chevron-up" : "bx-chevron-down"}`} />
                  </div>
                  <Collapse isOpen={openSections.basicDemographic}>
                    <div className="demo-section-body">
                      <div className="demo-section-content">
                        {/* Group Target and Maximum Samples with underline */}
                        <Row className="mb-3" style={{ paddingBottom: "16px", borderBottom: "1px solid #d5d7da" }}>
                          <Col md={6}>
                            <Label htmlFor={`groupTargetNumber-${card.id}`} className="form-label">
                              Group Target Number <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id={`groupTargetNumber-${card.id}`}
                              name="groupTargetNumber"
                              type="text"
                              className="form-control"
                              placeholder="某一小组所希望收取的样本数量"
                              value={card.groupTargetNumber || ""}
                              onChange={e => {
                                const value = e.target.value
                                if (value === '' || /^\d+$/.test(value)) {
                                  onUpdate(card.id, "groupTargetNumber", value)
                                }
                              }}
                              disabled={isDisabled}
                            />
                          </Col>
                          <Col md={6}>
                            <Label htmlFor={`groupMaximumSamples-${card.id}`} className="form-label">
                              Group Maximum Samples <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id={`groupMaximumSamples-${card.id}`}
                              name="groupMaximumSamples"
                              type="text"
                              className="form-control"
                              placeholder="该组最多需要收集的样本数量"
                              value={card.groupMaximumSamples || ""}
                              onChange={e => {
                                const value = e.target.value
                                if (value === '' || /^\d+$/.test(value)) {
                                  onUpdate(card.id, "groupMaximumSamples", value)
                                }
                              }}
                              disabled={isDisabled}
                            />
                          </Col>
                        </Row>
                        
                        {/* Gender row first */}
                        {renderRow(categoriesConfig[0])}
                        {/* City section with hierarchical structure */}
                        {renderCitySection()}
                        {/* Rest of the categories (skip gender which is index 0) */}
                        {categoriesConfig.slice(1).map(category => renderRow(category))}
                        {/* App Categories, Living Arrangement, Transportation - inside Basic Demographic */}
                        {additionalBasicCategories.map(category => renderRow(category))}
                      </div>
                    </div>
                  </Collapse>
                </div>

                {/* Car Related */}
                <div className="demo-section">
                  <div className="demo-section-header" onClick={() => toggleSection("carRelated")}>
                    <h6>{t("demographic.carRelated")}</h6>
                    <i className={`bx ${openSections.carRelated ? "bx-chevron-up" : "bx-chevron-down"}`} />
                  </div>
                  <Collapse isOpen={openSections.carRelated}>
                    <div className="demo-section-body">
                      <div className="demo-section-content">
                        {carRelatedCategories.map(category => renderRow(category))}
                      </div>
                    </div>
                  </Collapse>
                </div>

                {/* Planned Purchase */}
                <div className="demo-section">
                  <div className="demo-section-header" onClick={() => toggleSection("carPlannedPurchase")}>
                    <h6>{t("demographic.carPlannedPurchase")}</h6>
                    <i className={`bx ${openSections.carPlannedPurchase ? "bx-chevron-up" : "bx-chevron-down"}`} />
                  </div>
                  <Collapse isOpen={openSections.carPlannedPurchase}>
                    <div className="demo-section-body">
                      <div className="demo-section-content">
                        {plannedPurchaseCategories.map(category => renderRow(category))}
                      </div>
                    </div>
                  </Collapse>
                </div>
              </>
            )}

            {/* Group Target and Maximum Samples for non-China countries */}
            {!isChina && (
              <div style={{ padding: "20px 24px", background: "#fff", borderBottom: "1px solid #e9eaeb" }}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Label htmlFor={`groupTargetNumber-${card.id}`} className="form-label">
                      Group Target Number <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id={`groupTargetNumber-${card.id}`}
                      name="groupTargetNumber"
                      type="text"
                      className="form-control"
                      placeholder="某一小组所希望收取的样本数量"
                      value={card.groupTargetNumber || ""}
                      onChange={e => {
                        const value = e.target.value
                        if (value === '' || /^\d+$/.test(value)) {
                          onUpdate(card.id, "groupTargetNumber", value)
                        }
                      }}
                      disabled={isDisabled}
                    />
                  </Col>
                  <Col md={6}>
                    <Label htmlFor={`groupMaximumSamples-${card.id}`} className="form-label">
                      Group Maximum Samples <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id={`groupMaximumSamples-${card.id}`}
                      name="groupMaximumSamples"
                      type="text"
                      className="form-control"
                      placeholder="该组最多需要收集的样本数量"
                      value={card.groupMaximumSamples || ""}
                      onChange={e => {
                        const value = e.target.value
                        if (value === '' || /^\d+$/.test(value)) {
                          onUpdate(card.id, "groupMaximumSamples", value)
                        }
                      }}
                      disabled={isDisabled}
                    />
                  </Col>
                </Row>
              </div>
            )}

            {/* Specific Samples Requirement - shown for all countries */}
            <div className="demo-section">
              <div className="demo-section-header" onClick={() => toggleSection("specificRequirement")}>
                <h6>{t("demographic.specificRequirement")}</h6>
                <i className={`bx ${openSections.specificRequirement ? "bx-chevron-up" : "bx-chevron-down"}`} />
              </div>
              <Collapse isOpen={openSections.specificRequirement}>
                <div className="demo-section-body">
                  <div className="demo-section-content">
                    <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
                      {t("demographic.specificRequirementDesc")}
                    </p>
                    <Input
                      type="textarea"
                      rows="6"
                      className="demo-textarea"
                      placeholder={t("demographic.specificRequirementPlaceholder")}
                      value={card.specificRequirement || ""}
                      onChange={e => onUpdate(card.id, "specificRequirement", e.target.value)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </Collapse>
            </div>

            {/* Delete Button */}
            {pageView !== "view" && (
              <div className="demo-footer">
                <button className="demo-delete-btn" onClick={() => onDelete(card.id)}>
                  <i className="bx bx-trash" />
                  {t("demographic.delete")}
                </button>
              </div>
            )}
          </div>
        </Collapse>
      </div>
    </>
  )
}

DemographicCard.propTypes = {
  card: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  pageView: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  countryTarget: PropTypes.string,
}

export default DemographicCard
